import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  markTicketAsRead,
  markLocationAsVisited,
  changeTicketContact,
  requestTicketUpdate,
  closeTicket,
  changeTicketStatus,
  submitFormToCreateCase,
  deleteRecentSearch,
  submitKBFeedback,
  markAssetViewed,
  updateCaseSubscription,
  markUserOnboarding,
  changeUserNotificationPreference,
  submitCasePrompt,
  trackUserEvent,
  manageMemberships,
  clearUserEventInfo,
  createServiceEvent,
  deleteServiceEvent,
  deleteServiceEventAttachments,
  updateServiceEvent,
  submitSearchFeedback,
} from './requests';
import { useNotificationsStore } from '../../../stores/globalNotificationStore';
import { callEvent } from '../../Amplitude/AmplitudeUtilities';
import { ErrorInterface } from '../../../interfaces/ErrorInterface';
import { FormSubmissionVariables } from '../../../interfaces/SupportFormInterface';
import { useNavigate } from 'react-router-dom';
import { usePaginationStore } from '../../../stores/paginationStore';
import { Dispatch, SetStateAction } from 'react';
import {
  updateSubscriptionListSubscribe,
  updateSubscriptionListUnsubscribe,
} from '../../Airship/AirshipUtilities';
import {
  AssetViewedInput,
  AssetServiceEvent,
  AssetServiceEventSource,
  AssetServiceEventStatus,
  AssetServiceHistory,
  Case,
  CaseStatus,
  ChangeCaseStatusMutationVariables,
  ChangeNotificationPreferenceMutationVariables,
  CloseCaseMutationVariables,
  CreateServiceEventMutationVariables,
  DeleteAssetServiceAttachmentsMutationVariables,
  DeleteAssetServiceEventMutationVariables,
  UpdateAssetServiceEventMutationVariables,
  DeleteRecentSearchesMutationVariables,
  ManageCaseSubscriptionMutationVariables,
  ManageProgramMembershipMutationVariables,
  MarkCaseReadMutationVariables,
  RequestCaseUpdateMutationVariables,
  SubmitCasePromptMutationVariables,
  SubmitKBFeedbackMutationVariables,
  User,
  UserEventFilter,
  UserEventInput,
  SubmitSearchFeedbackMutationVariables,
} from '../../../API';
import { FileWithPreview } from '../../../interfaces/AttachmentInterface';

export const useMarkTicketAsRead = (
  caseId: MarkCaseReadMutationVariables['caseId']
) =>
  // TODO: Possibly update query cache as a result of mutation
  useMutation({
    mutationKey: ['ticket', 'mark-read'],
    mutationFn: () => markTicketAsRead({ caseId }),
  });

export const useMarkLocationAsVisited = (
  locationNum?: string,
  locationName?: string
) =>
  useMutation({
    mutationKey: ['location', 'mark-visited'],
    mutationFn: () => markLocationAsVisited(locationNum, locationName),
  });

export const useChangeTicketContact = (
  ticket: Case,
  selectedChangeContact: User,
  activeLocation: string,
  setShowChangeModal: (arg: boolean) => void,
  subscribeViaMessageBlock: () => void,
  showContactChangeMessageBlock?: boolean,
  setTimeLeft?: (value: number | undefined) => void
) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationsStore();
  return useMutation({
    mutationKey: ['ticket', 'change-contact'],
    mutationFn: () =>
      changeTicketContact({
        caseId: ticket?.id,
        contactId: selectedChangeContact?.id,
        contactGuid: selectedChangeContact?.guid,
        previousContactGuid: ticket?.contact?.guid,
        locationNum: activeLocation,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['get-ticket', ticket?.id] });

      const previousDetails = queryClient.getQueryData([
        'get-ticket',
        ticket?.id,
      ]);
      // Optimistically update to the new value
      queryClient.setQueryData(
        ['get-ticket', ticket?.id],
        (oldData: unknown) => {
          return oldData
            ? {
                ...oldData,
                contact: {
                  firstName: selectedChangeContact?.firstName,
                  fullName: selectedChangeContact?.fullName,
                  guid: selectedChangeContact?.guid,
                  lastName: selectedChangeContact?.lastName,
                },
              }
            : oldData;
        }
      );
      return { previousDetails };
    },
    onError: (error, variables, context) => {
      context &&
        queryClient.setQueryData(
          ['get-ticket', ticket?.id],
          context.previousDetails
        );
    },
    onSuccess: () => {
      if (showContactChangeMessageBlock) {
        addNotification({
          severity: 'success',
          regularTextContent: ` is now the ticket contact and will receive notifications for this ticket. \nSubscribe to this ticket if you also want to receive notifications.`,
          boldTextContent: `${selectedChangeContact?.fullName}`,
          contentReverse: false,
          actionText: 'Subscribe to Updates',
          actionLinkOnClick: () => {
            subscribeViaMessageBlock();
            setTimeLeft && setTimeLeft(60);
          },
        });
      } else {
        addNotification({
          severity: 'success',
          regularTextContent: 'Ticket contact has been changed to',
          boldTextContent: `${selectedChangeContact?.fullName}.`,
          contentReverse: true,
          timeToDisplay: 5000,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['get-ticket', ticket?.id],
      });
      callEvent('change case contact', {
        'case number': ticket?.number,
        locationNum: activeLocation,
      });
    },
    onSettled: () => {
      setShowChangeModal(false);
      queryClient.invalidateQueries({
        queryKey: ['all-tickets', activeLocation],
        refetchType: 'all', //removed refetchInactive: true as it is not supported and replaced with refetchType: 'all' (fixed in tanstack/react-query upgrade)
      });
    },
  });
};

export const useCloseTicket = (
  caseId: CloseCaseMutationVariables['caseId'],
  ticketNumber: string,
  activeLocation: string,
  setTicketJustClosed: (arg: boolean) => void,
  setTicketCanClose?: (arg: boolean) => void,
  trackForbiddenEvent = false
) => {
  const queryClient = useQueryClient();
  const { clearNotifications } = useNotificationsStore();
  return useMutation({
    mutationKey: ['ticket', 'close'],
    mutationFn: (comment?: CloseCaseMutationVariables['comment']) =>
      closeTicket({ caseId, comment }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['get-ticket', caseId] });

      const previousDetails = queryClient.getQueryData(['get-ticket', caseId]);

      return { previousDetails };
    },
    onError: (error: ErrorInterface) => {
      const gqlError = error.response?.errors?.[0];
      if (gqlError?.errorInfo === 403) {
        setTicketCanClose && setTicketCanClose(false);
        queryClient.invalidateQueries({
          queryKey: ['get-ticket', caseId],
        });
        if (trackForbiddenEvent) {
          callEvent('close case 403 result', {
            'case number': ticketNumber,
            error: gqlError.message ?? gqlError.errorType,
            locationNum: activeLocation,
          });
        }
      }
      callEvent('close case error', {
        'case number': ticketNumber,
        locationNum: activeLocation,
      });
    },
    onSuccess: () => {
      clearNotifications();
      setTicketJustClosed(true);
      callEvent('close case', {
        'case number': ticketNumber,
        locationNum: activeLocation,
      });
    },
    onSettled: data => {
      queryClient.setQueryData(['get-ticket', caseId], (oldData: unknown) => {
        return oldData
          ? {
              ...oldData,
              ...data,
            }
          : oldData;
      });
      setTimeout(() => {
        setTicketJustClosed(false);
      }, 2000);
      queryClient.invalidateQueries({
        queryKey: ['all-tickets', activeLocation],
        refetchType: 'all', //removed refetchInactive: true as it is not supported and replaced with refetchType: 'all' (fixed in tanstack/react-query upgrade)
      });
    },
  });
};

export const useRequestTicketUpdate = (
  caseId: RequestCaseUpdateMutationVariables['caseId'],
  activeLocation: string
) => {
  const queryClient = useQueryClient();
  const { addNotification, setLoadingNotification } = useNotificationsStore();
  return useMutation({
    mutationKey: ['ticket', 'request-update'],
    mutationFn: () => requestTicketUpdate({ caseId }),
    onMutate: () => {
      setLoadingNotification();
      queryClient.cancelQueries({
        queryKey: ['get-ticket', caseId],
      });

      const previousDetails = queryClient.getQueryData(['get-ticket', caseId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['get-ticket', caseId], (oldData: unknown) => {
        return oldData
          ? {
              ...oldData,
              canRequestUpdate: false,
            }
          : oldData;
      });
      return { previousDetails };
    },
    onError: (error, variables, context) => {
      context &&
        queryClient.setQueryData(
          ['get-ticket', caseId],
          context.previousDetails
        );
    },
    onSuccess: () => {
      addNotification({
        severity: 'success',
        regularTextContent: 'Status Requested.',
        boldTextContent:
          'Request Status for this case will be available again in 24 hours.',
        contentReverse: true,
        timeToDisplay: 5000,
      });
      queryClient.invalidateQueries({
        queryKey: ['get-ticket', caseId],
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['all-tickets', activeLocation],
        refetchType: 'all', //removed refetchInactive: true as it is not supported and replaced with refetchType: 'all' (fixed in tanstack/react-query upgrade)
      });
    },
  });
};

export const useReopenTicket = (
  caseId: ChangeCaseStatusMutationVariables['caseId'],
  ticketNumber: string,
  activeLocation: string,
  setRetryLoading: (arg: boolean) => void
) => {
  const queryClient = useQueryClient();
  const { clearNotifications } = useNotificationsStore();
  return useMutation({
    mutationKey: ['ticket', 'reopen'],
    mutationFn: (comment: ChangeCaseStatusMutationVariables['comment']) =>
      changeTicketStatus({ status: CaseStatus.OPEN, caseId, comment }),
    onMutate: async () => {
      clearNotifications();
      callEvent('reopen ticket', {
        'case number': ticketNumber,
        locationNum: activeLocation,
      });
      await queryClient.cancelQueries({ queryKey: ['get-ticket', caseId] });
    },
    onError: () => {
      callEvent('reopen ticket error', {
        'case number': ticketNumber,
        locationNum: activeLocation,
      });
    },
    onSettled: data => {
      setRetryLoading(false);
      queryClient.invalidateQueries({
        queryKey: ['all-tickets', activeLocation],
        refetchType: 'all', //removed refetchInactive: true as it is not supported and replaced with refetchType: 'all' (fixed in tanstack/react-query upgrade)
      });
      queryClient.setQueryData(['get-ticket', caseId], (oldData: unknown) => {
        return oldData
          ? {
              ...oldData,
              ...data,
            }
          : oldData;
      });
    },
  });
};

export const useSubmitFormToCreateCase = (
  formID: string,
  redirect = true,
  onViewTicket?: (
    ticketId: string,
    locationNum: string,
    attachmentErrors: FileWithPreview[],
    formID: string
  ) => void
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTicketPageNumber } = usePaginationStore();
  return useMutation({
    mutationKey: ['form', 'submit'],
    mutationFn: ({
      locationNum,
      variables,
      formTitle,
      formAttachments,
    }: FormSubmissionVariables) =>
      submitFormToCreateCase(
        locationNum,
        formID,
        variables,
        formTitle,
        formAttachments
      ),
    onError: (error: ErrorInterface, variables) => {
      callEvent('form submit error', {
        'form id': formID,
        locationNum: variables.locationNum,
        'error message': error.response?.errors?.[0].errorType,
      });
    },
    onSuccess: (data, variables) => {
      const { id, attachmentErrors, confirmationMessages } = data;
      callEvent('submit form success', {
        'form id': formID,
        locationNum: variables.locationNum,
        'form name': variables.formTitle,
      });
      setTicketPageNumber(1);
      if (redirect && !onViewTicket) {
        navigate(`/tickets/${variables.locationNum}/${id}`, {
          state: {
            newTicket: true,
            attachmentErrors,
            formID,
            confirmationMessages,
          },
        });
      }
      if (onViewTicket) {
        onViewTicket(id, variables.locationNum, attachmentErrors, formID);
      }
      if (formID === 'major_issue_assoc') {
        const majorCaseId = variables.variables.find(
          o => o.id === 'parent_case'
        )?.value;
        queryClient.invalidateQueries({
          queryKey: ['get-related-minor-cases', majorCaseId],
        });
      }
    },
  });
};

export const useDeleteRecentSearch = (
  userGuid: DeleteRecentSearchesMutationVariables['userGuid'],
  contains: DeleteRecentSearchesMutationVariables['contains'],
  activeLocation?: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['search', 'delete-recent'],
    mutationFn: () => deleteRecentSearch({ userGuid, contains }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['get-recent-searches', userGuid],
      });
    },
    onSuccess: async () => {
      const eventName =
        contains.length === 0
          ? 'remove all recent searches'
          : 'remove recent search';
      const payload =
        contains.length === 0
          ? {
              'search term': 'all recent searches',
              locationNum: activeLocation,
            }
          : { 'search term': contains[0] ?? '' };
      callEvent(eventName, payload);
      await queryClient.invalidateQueries({
        queryKey: ['get-recent-searches', userGuid],
      });
    },
    onError: error => {
      console.error('Mutation failed:', error);
    },
  });
};

export const useSubmitKBFeedback = () =>
  useMutation({
    mutationKey: ['kb', 'submit-feedback'],
    mutationFn: ({
      articleID,
      positive,
      reasonCode,
      comment,
    }: SubmitKBFeedbackMutationVariables) =>
      submitKBFeedback({ articleID, positive, reasonCode, comment }),
  });

export const useMarkAssetViewed = (input: AssetViewedInput) =>
  useMutation({
    mutationKey: ['asset', 'mark-viewed'],
    mutationFn: () => markAssetViewed(input),
  });

export const useUpdateSubscription = (
  caseId: ManageCaseSubscriptionMutationVariables['caseId'],
  subscribe: ManageCaseSubscriptionMutationVariables['subscribe'],
  activeLocation: string,
  caseNumber: string,
  isSmallScreen: boolean,
  setIsSubscribed: Dispatch<SetStateAction<boolean>>,
  assignToMeSelected: boolean,
  guid: string
) => {
  const { addNotification, setLoadingNotification } = useNotificationsStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['ticket', 'update-subscription'],
    mutationFn: () => updateCaseSubscription({ caseId, subscribe }),
    onMutate: () => isSmallScreen && setLoadingNotification(),
    onSuccess: data => {
      setIsSubscribed(data);
      !assignToMeSelected &&
        addNotification({
          severity: 'success',
          boldTextContent: subscribe
            ? `Subscribed to ticket updates.`
            : `Unsubscribed from ticket updates.`,
          regularTextContent: subscribe
            ? `You will now receive notifications for any updates on this ticket.`
            : `You will no longer receive notifications for any updates on this ticket.`,
          timeToDisplay: 5000,
        });

      callEvent(!subscribe ? 'unsubscribe from case' : 'subscribe to case', {
        locationNum: activeLocation,
        'case number': caseNumber,
      });

      queryClient.invalidateQueries({
        queryKey: ['get-airship-user-properties', guid],
      });
    },
    onError: () => {
      callEvent('subscribe or unsubscribe error', {
        locationNum: activeLocation,
        'case number': caseNumber,
      });
    },
  });
};

export const useMarkUserOnboarding = (status: string) =>
  useMutation({
    mutationKey: ['user', 'mark-onboarding'],
    mutationFn: () => markUserOnboarding(status),
  });

export const useChangeUserNotificationPreference = (
  toggleOn: ChangeNotificationPreferenceMutationVariables['toggleOn'],
  type: ChangeNotificationPreferenceMutationVariables['type']
) =>
  useMutation({
    mutationKey: ['user', 'change-notification-preference'],
    mutationFn: () => changeUserNotificationPreference({ toggleOn, type }),
    onSuccess: () => {
      //runs subscribe/unsubscribe via Airship SDK to keep lists in sync locally
      toggleOn
        ? updateSubscriptionListSubscribe('sub_case_update_alerts_email')
        : updateSubscriptionListUnsubscribe('sub_case_update_alerts_email');
    },
  });

export const useSubmitCasePrompt = (
  input: SubmitCasePromptMutationVariables['input']
) =>
  useMutation({
    mutationKey: ['ticket', 'submit-case-prompt'],
    mutationFn: () => submitCasePrompt({ input }),
  });

export const useTrackUserEvent = (
  eventType: UserEventInput['eventType'],
  filter?: UserEventFilter
) => {
  // TODO: Possibly update query cache as a result of mutation
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['user', 'track-event'],
    mutationFn: () => trackUserEvent({ input: { eventType } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['get-user-event-info', eventType, filter],
      });
    },
  });
};

export const useManageMemberships = (
  program: ManageProgramMembershipMutationVariables['program'],
  userGuid: ManageProgramMembershipMutationVariables['userGuid']
) =>
  useMutation({
    mutationKey: ['user', 'manage-program-membership'],
    mutationFn: ({
      membership,
      locationNum,
    }: {
      membership: ManageProgramMembershipMutationVariables['membership'];
      locationNum: ManageProgramMembershipMutationVariables['locationNum'];
    }) =>
      manageMemberships({
        program,
        membership,
        locationNum,
        userGuid,
      }),
  });

export const useClearUserEventInfo = (eventTypes: string[]) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationsStore();

  const notifyFailure = () =>
    addNotification({
      severity: 'error',
      regularTextContent: `Failed to clear event history for one or more events: ${eventTypes.join(', ')}.`,
    });

  return useMutation({
    mutationKey: ['user', 'clear-event-info'],
    mutationFn: () =>
      Promise.all(eventTypes.map(eventType => clearUserEventInfo(eventType))),

    onSuccess: data => {
      // The server answers with a boolean per event type; only report success
      // when every one of them was actually cleared.
      if (data.length === 0 || !data.every(Boolean)) {
        notifyFailure();
        return;
      }

      addNotification({
        severity: 'success',
        regularTextContent: `Event history cleared for: ${eventTypes.join(', ')}.`,
        timeToDisplay: 3000,
      });

      eventTypes.forEach(eventType => {
        queryClient.invalidateQueries({
          queryKey: ['get-user-event-info', eventType],
        });
      });
    },

    onError: error => {
      notifyFailure();
      console.error('Clear User Event Info Error:', error);
    },
  });
};

/**
 * Hook for creating a new service event record with optimistic UI updates.
 *
 * The core idea: when the user submits the "Add Record" form, we don't wait
 * for the server round-trip. Instead we immediately inject a fake event into
 * the React Query cache so the service history list updates instantly. The
 * server call happens in the background. Once it resolves we reconcile:
 *
 * ── Lifecycle ──────────────────────────────────────────────────────────
 *
 *   mutate() called
 *       │
 *       ▼
 *   onMutate  (runs BEFORE the network request)
 *       • Cancels any in-flight service-history refetches so they can't
 *         overwrite the optimistic data we're about to write.
 *       • Snapshots the current cache so we can roll back on failure.
 *       • Builds a temporary event object (temp ID, isSaving: true) from
 *         the form values and inserts it into the cached events list,
 *         sorted newest-first. Also bumps totalCostUSD.
 *       • Returns the snapshot as `context` for onError.
 *       │
 *       ▼
 *   mutationFn  (the actual POST / GraphQL call to createServiceEvent)
 *       │
 *    ┌──┴──┐
 *    ▼     ▼
 * onSuccess  onError
 *    │        │
 *    │        • If _skipRollbackOnError is set, just logs and returns
 *    │          (used when the caller handles retry logic itself).
 *    │        • Otherwise restores the snapshot from onMutate, which
 *    │          removes the optimistic event and reverts totalCostUSD.
 *    │        • Shows an error toast.
 *    │
 *    • If the caller has no attachments (`_hasAttachments` is false),
 *      invalidates the service-history query so React Query refetches
 *      real data from the server, replacing the optimistic "Saving..."
 *      record with the server's authoritative response.
 *    • If the caller has attachments (`_hasAttachments` is true),
 *      invalidation is skipped — the caller must invalidate the query
 *      itself after all attachment uploads complete.
 *
 * ── Retry handling ─────────────────────────────────────────────────────
 *
 * When `_isRetry` is true, onMutate skips creating a second optimistic
 * event — the one from the original attempt is still in the cache. It
 * only re-snapshots the current state for rollback purposes.
 *
 * `_skipRollbackOnError` lets the caller suppress the automatic rollback
 * in onError so it can orchestrate its own recovery (e.g. showing a
 * retry confirmation dialog while keeping the optimistic record visible).
 *
 * @param locationNum - Restaurant location number (used as part of the cache key)
 * @param assetId     - The specific asset whose service history we're updating
 */
type CreateServiceEventVariablesWithOptions =
  CreateServiceEventMutationVariables & {
    _isRetry?: boolean;
    _skipRollbackOnError?: boolean;
    _hasAttachments?: boolean;
  };

export const useCreateServiceEvent = (locationNum: string, assetId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['service-event', 'create'],
    mutationFn: (variables: CreateServiceEventVariablesWithOptions) =>
      createServiceEvent(variables.input),

    onMutate: async variables => {
      const opts = variables as CreateServiceEventVariablesWithOptions;

      // On a retry the optimistic event from the first attempt is already
      // in the cache, so we just grab a fresh snapshot for rollback and
      // skip inserting a duplicate.
      if (opts._isRetry) {
        await queryClient.cancelQueries({
          queryKey: ['get-service-history', locationNum, assetId],
        });
        const previousServiceHistory =
          queryClient.getQueryData<AssetServiceHistory>([
            'get-service-history',
            locationNum,
            assetId,
          ]);
        return { previousServiceHistory };
      }

      // Cancel in-flight refetches so a slow response from an earlier
      // query doesn't land after our setQueryData and overwrite it.
      await queryClient.cancelQueries({
        queryKey: ['get-service-history', locationNum, assetId],
      });

      // Snapshot current cache — this is what we restore if the mutation fails.
      const previousServiceHistory =
        queryClient.getQueryData<AssetServiceHistory>([
          'get-service-history',
          locationNum,
          assetId,
        ]);

      // Build a fake event that looks like what the server will eventually
      // return. The temp ID is unique per attempt (Date.now) and `isSaving`
      // tells the UI to render a "Saving..." indicator on this row.
      const optimisticEvent: AssetServiceEvent & { isSaving: boolean } = {
        __typename: 'AssetServiceEvent' as const,
        id: `temp-${Date.now()}`,
        assetId: variables.input.assetId,
        locationNum: variables.input.locationNum,
        status: AssetServiceEventStatus.COMPLETED,
        costUSD: variables.input.costUSD || '0',
        serviceDate: variables.input.serviceDate,
        issue: variables.input.issue,
        resolution: variables.input.resolution,
        technicianName: variables.input.technicianName,
        source: AssetServiceEventSource.CFA_SUPPORT,
        vendor: variables.input.vendor,
        createdOn: new Date().toISOString(),
        updatedOn: new Date().toISOString(),
        isSaving: true,
      };

      // Write the optimistic event into the cache. We merge it into the
      // existing events array and re-sort by serviceDate descending so it
      // slots into the correct chronological position (users can back-date
      // records). We also eagerly update totalCostUSD so the footer total
      // reflects the new record immediately.
      queryClient.setQueryData<AssetServiceHistory>(
        ['get-service-history', locationNum, assetId],
        old => {
          if (!old) return old;

          const updatedEvents = [optimisticEvent, ...(old.events || [])].sort(
            (a, b) => {
              const dateA = new Date(a?.serviceDate || 0).getTime();
              const dateB = new Date(b?.serviceDate || 0).getTime();
              return dateB - dateA;
            }
          );

          return {
            ...old,
            events: updatedEvents as Array<AssetServiceEvent | null>,
            totalCostUSD: String(
              Number(old.totalCostUSD || 0) +
                Number(variables.input.costUSD || 0)
            ),
          };
        }
      );

      return { previousServiceHistory };
    },

    onSuccess: (_data, variables) => {
      const opts = variables as CreateServiceEventVariablesWithOptions;
      // When the caller has attachments to upload, it handles invalidation
      // itself after all uploads complete. Otherwise, invalidate now so the
      // refetch replaces the optimistic "Saving..." record with real data.
      if (!opts._hasAttachments) {
        queryClient.invalidateQueries({
          queryKey: ['get-service-history', locationNum, assetId],
        });
      }
    },

    onError: (error, variables, context) => {
      const skipRollback = (variables as CreateServiceEventVariablesWithOptions)
        ._skipRollbackOnError;

      // When the caller is handling its own retry/recovery flow it passes
      // _skipRollbackOnError so the optimistic record stays visible while
      // the user decides what to do (e.g. a "Retry?" dialog).
      if (skipRollback) {
        console.error('Create Service Event Error:', error);
        return;
      }

      // Standard path: put the cache back exactly how it was before
      // onMutate ran, which removes the optimistic event and reverts
      // the totalCostUSD change.
      if (context?.previousServiceHistory) {
        queryClient.setQueryData(
          ['get-service-history', locationNum, assetId],
          context.previousServiceHistory
        );
      }

      console.error('Create Service Event Error:', error);
    },
  });
};

export const useDeleteServiceEvent = (locationNum: string, assetId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['service-event', 'delete'],
    mutationFn: (variables: DeleteAssetServiceEventMutationVariables) =>
      deleteServiceEvent(variables.input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get-service-history', locationNum, assetId],
      });
    },
  });
};

export const useDeleteServiceEventAttachments = (
  locationNum: string,
  assetId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['service-event', 'delete-attachments'],
    mutationFn: (variables: DeleteAssetServiceAttachmentsMutationVariables) =>
      deleteServiceEventAttachments(variables.input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['get-service-history', locationNum, assetId],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-service-event', variables.input.serviceEventId],
      });
    },
  });
};

// _hasAttachments mirrors the create flow: when the caller has attachments to
// upload it invalidates the list itself after the uploads finish, so the hook
// skips invalidation to avoid a premature refetch that would drop the just-
// uploaded files.
type UpdateServiceEventVariablesWithOptions =
  UpdateAssetServiceEventMutationVariables & {
    _hasAttachments?: boolean;
  };

export const useUpdateServiceEvent = (locationNum: string, assetId: string) => {
  const queryClient = useQueryClient();
  const serviceHistoryQueryKey = ['get-service-history', locationNum, assetId];

  return useMutation({
    mutationKey: ['service-event', 'update'],
    mutationFn: (variables: UpdateServiceEventVariablesWithOptions) =>
      updateServiceEvent(variables.input),

    // Optimistically patch the edited event in the cached list so the row shows
    // its new values with a "Saving..." indicator immediately — mirrors the
    // create flow, but updates the existing record in place rather than
    // inserting a new one.
    onMutate: async variables => {
      // Cancel in-flight refetches so a slow response can't overwrite our
      // optimistic edit.
      await queryClient.cancelQueries({ queryKey: serviceHistoryQueryKey });

      // Snapshot the cache so onError can roll back to it.
      const previousServiceHistory =
        queryClient.getQueryData<AssetServiceHistory>(serviceHistoryQueryKey);

      queryClient.setQueryData<AssetServiceHistory>(
        serviceHistoryQueryKey,
        old => {
          if (!old) return old;

          // Overlay the submitted values onto the matching event and mark it
          // isSaving. Track the cost delta so the running total stays correct.
          let costDelta = 0;
          const updatedEvents = (old.events || []).map(event => {
            if (!event || event.id !== variables.input.id) return event;

            costDelta =
              Number(variables.input.costUSD || 0) - Number(event.costUSD || 0);

            return {
              ...event,
              serviceDate: variables.input.serviceDate ?? event.serviceDate,
              issue: variables.input.issue,
              resolution: variables.input.resolution,
              vendor: variables.input.vendor,
              technicianName: variables.input.technicianName,
              costUSD: variables.input.costUSD ?? event.costUSD,
              isSaving: true,
            } as AssetServiceEvent & { isSaving: boolean };
          });

          // Re-sort newest-first since serviceDate may have changed.
          const sortedEvents = [...updatedEvents].sort((a, b) => {
            const dateA = new Date(a?.serviceDate || 0).getTime();
            const dateB = new Date(b?.serviceDate || 0).getTime();
            return dateB - dateA;
          });

          return {
            ...old,
            events: sortedEvents as Array<AssetServiceEvent | null>,
            totalCostUSD: String(Number(old.totalCostUSD || 0) + costDelta),
          };
        }
      );

      return { previousServiceHistory };
    },

    onError: (_error, _variables, context) => {
      // Roll back the optimistic edit. (Fuller retry UX is handled by the
      // drawer's error modal.)
      if (context?.previousServiceHistory) {
        queryClient.setQueryData(
          serviceHistoryQueryKey,
          context.previousServiceHistory
        );
      }
    },

    onSuccess: (_data, variables) => {
      // When the caller has attachments to upload it invalidates the list
      // itself after the uploads finish; otherwise invalidate now so the
      // optimistic "Saving..." row is replaced by the server's real data.
      if (!variables._hasAttachments) {
        queryClient.invalidateQueries({ queryKey: serviceHistoryQueryKey });
      }
    },
  });
};

export const useSubmitSearchFeedback = () =>
  useMutation({
    mutationKey: ['search', 'submit-feedback'],
    mutationFn: (variables: SubmitSearchFeedbackMutationVariables) =>
      submitSearchFeedback(variables),
  });
